import { Card, CardContent } from '@/components/ui/Card';
import { formatDate, formatDateTime } from '@/lib/utils';
import type { OficinaBoardOrder } from '@/services/oficinaApi';
import { OficinaMercosulPlate } from '@/components/oficina/OficinaMercosulPlate';

function veiculoLabel(o: OficinaBoardOrder): string {
  const parts = [o.veiculoMarca, o.veiculoModelo].filter(Boolean).join(' ');
  if (o.veiculoAnoFabricacao != null) {
    return parts ? `${parts} / ${o.veiculoAnoFabricacao}` : String(o.veiculoAnoFabricacao);
  }
  return parts || '—';
}

function isSaidaAtrasada(dataPrevistaSaida: string, now: Date): boolean {
  const prev = new Date(dataPrevistaSaida).getTime();
  if (Number.isNaN(prev)) return false;
  return prev < now.getTime();
}

export type OficinaKanbanOrderCardProps = {
  order: OficinaBoardOrder;
  now: Date;
  /** Tipografia e placa menores (Painel v2). */
  compact?: boolean;
};

export function OficinaKanbanOrderCard({ order: o, now, compact }: OficinaKanbanOrderCardProps) {
  const atrasada = isSaidaAtrasada(o.dataPrevistaSaida, now);

  if (compact) {
    return (
      <Card className="w-full max-w-none border border-border/70 bg-background/70 shadow-none transition-all duration-200 hover:-translate-y-0.5 hover:bg-background hover:shadow-md">
        <CardContent className="flex w-full min-w-0 gap-2 p-2 text-[11px] leading-snug sm:text-xs">
          <div className="min-w-0 flex-1 space-y-1">
            <div className="space-y-0.5">
              <span className="block truncate font-semibold text-[13px] leading-snug text-yellow-500 dark:text-yellow-400">
                {o.clienteNome}
              </span>
              <span
                className="inline-block rounded border border-border/70 bg-muted/40 px-1 py-0 font-mono text-[10px] font-semibold tabular-nums text-foreground"
                title="Número da locação (id_locacao)"
              >
                Loc. {o.numeroLocacao ?? '—'}
              </span>
            </div>
            <p className="text-foreground/90">
              <span className="text-muted-foreground">OS:</span> {o.osCodigoErp}
            </p>
            <p className="text-foreground/90">
              <span className="text-muted-foreground">Entrada:</span> {formatDate(o.dataEntrada)}
            </p>
            <p className={atrasada ? 'font-medium text-red-500' : 'text-foreground/90'}>
              <span className="text-muted-foreground">Previsão saída:</span> {formatDateTime(o.dataPrevistaSaida)}
            </p>
            <p className="break-words text-foreground/90">
              <span className="text-muted-foreground">Veículo:</span> {veiculoLabel(o)}
              {o.corVeiculoTexto ? (
                <span className="text-muted-foreground"> · {o.corVeiculoTexto}</span>
              ) : null}
            </p>
            {o.tecnicosNomes ? (
              <p className="break-words text-foreground/90" title="Técnicos (tecnicos_nomes / API)">
                <span className="text-muted-foreground">Técnico:</span> {o.tecnicosNomes}
              </p>
            ) : null}
            <p className="text-foreground/90">
              <span className="text-muted-foreground">Dias na oficina:</span>{' '}
              <span className="font-semibold text-foreground">{o.diasDesdeEntrada}</span>
            </p>
          </div>
          <div className="flex shrink-0 items-start justify-end self-stretch pt-0.5">
            <OficinaMercosulPlate placa={o.veiculoPlaca} compact />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-none border border-border/70 bg-background/70 shadow-none transition-all duration-200 hover:-translate-y-0.5 hover:bg-background hover:shadow-md">
      <CardContent className="flex w-full min-w-0 gap-3 p-4 text-[15px] leading-snug">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="space-y-1">
            <span className="block truncate font-semibold text-[19.5px] leading-snug text-yellow-500 dark:text-yellow-400">
              {o.clienteNome}
            </span>
            <span
              className="inline-block rounded-md border border-border/70 bg-muted/40 px-2 py-0.5 font-mono text-xs font-semibold tabular-nums text-foreground"
              title="Número da locação (id_locacao)"
            >
              Loc. {o.numeroLocacao ?? '—'}
            </span>
          </div>
          <p className="text-foreground/90">
            <span className="text-muted-foreground">OS:</span> {o.osCodigoErp}
          </p>
          <p className="text-foreground/90">
            <span className="text-muted-foreground">Entrada:</span> {formatDate(o.dataEntrada)}
          </p>
          <p className={atrasada ? 'font-medium text-red-500' : 'text-foreground/90'}>
            <span className="text-muted-foreground">Previsão saída:</span> {formatDateTime(o.dataPrevistaSaida)}
          </p>
          <p className="text-foreground/90">
            <span className="text-muted-foreground">Veículo:</span> {veiculoLabel(o)}
            {o.corVeiculoTexto ? (
              <span className="text-muted-foreground"> · {o.corVeiculoTexto}</span>
            ) : null}
          </p>
          {o.tecnicosNomes ? (
            <p className="text-foreground/90" title="Técnicos (tecnicos_nomes / API)">
              <span className="text-muted-foreground">Técnico:</span> {o.tecnicosNomes}
            </p>
          ) : null}
          <p className="text-foreground/90">
            <span className="text-muted-foreground">Dias na oficina:</span>{' '}
            <span className="font-semibold text-foreground">{o.diasDesdeEntrada}</span>
          </p>
        </div>
        <div className="flex shrink-0 items-center justify-end self-stretch">
          <OficinaMercosulPlate placa={o.veiculoPlaca} />
        </div>
      </CardContent>
    </Card>
  );
}
